import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf, Brain, Sparkles, Moon, Heart } from 'lucide-react';
export function IngredientBreakdown() {
  const [selectedIngredient, setSelectedIngredient] = useState<number | null>(null);
  const ingredients = [{
    name: 'Valerian Root Extract',
    dosage: '600mg',
    icon: Moon,
    color: 'from-purple-500 to-indigo-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    image: 'https://lyrasleep.com/cdn/shop/files/valerian-officinalis-plant-root-1296-728-header.webp?v=1758717730&width=80',
    benefits: ['Reduces anxiety', 'Promotes deep sleep', 'Calms nervous system'],
    description: 'Clinical dose proven to reduce sleep latency and improve sleep quality'
  }, {
    name: 'L-Theanine',
    dosage: '200mg',
    icon: Brain,
    color: 'from-blue-500 to-cyan-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    image: 'https://lyrasleep.com/cdn/shop/files/images_11.jpg?v=1758717730&width=80',
    benefits: ['Reduces mental stress', 'Promotes relaxation', 'Enhances sleep quality'],
    description: 'Amino acid that promotes alpha brain waves for calm focus'
  }, {
    name: 'Magnesium Glycinate',
    dosage: '350mg',
    icon: Sparkles,
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    image: 'https://lyrasleep.com/cdn/shop/files/magnesium-glycinate-powder-500x500.webp?v=1758717300&width=80',
    benefits: ['Relaxes muscles', 'Regulates sleep cycles', 'No digestive issues'],
    description: 'Highly bioavailable form that supports deep, restorative sleep'
  }, {
    name: '5-HTP',
    dosage: '200mg',
    icon: Heart,
    color: 'from-pink-500 to-rose-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    image: 'https://lyrasleep.com/cdn/shop/files/5-htp-foods.webp?v=1758717731&width=80',
    benefits: ['Boosts serotonin', 'Improves mood', 'Supports REM sleep'],
    description: 'Precursor to serotonin for better sleep and mood regulation'
  }, {
    name: 'Passionflower Extract',
    dosage: '400mg',
    icon: Leaf,
    color: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    image: 'https://lyrasleep.com/cdn/shop/files/HerbalLibrary_Gallery_1_PassionFlower_720x720_0cd4f424-0254-400e-ab8f-64246494ba1b.webp?v=1758717731&width=80',
    benefits: ['Reduces restlessness', 'Eases anxiety', 'Gentle sedative effect'],
    description: 'Traditional herb that increases GABA for natural relaxation'
  }];
  return <div className="my-16 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border-2 border-indigo-100" data-id="element-242">
      <div className="text-center mb-8" data-id="element-243">
        <div className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4" data-id="element-244">
          <Sparkles className="w-4 h-4" data-id="element-245" />
          CLINICAL-DOSE INGREDIENTS
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-246">
          Every Ingredient at Full Clinical Strength
        </h3>
        <p className="text-gray-600" data-id="element-247">
          Not "fairy dust" doses — real amounts backed by research
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-248">
        {ingredients.map((ingredient, index) => {
        const Icon = ingredient.icon;
        const isSelected = selectedIngredient === index;
        return <motion.div key={ingredient.name} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          scale: 1.02
        }} onClick={() => setSelectedIngredient(isSelected ? null : index)} className={`${ingredient.bgColor} ${ingredient.borderColor} border-2 rounded-xl overflow-hidden cursor-pointer transition-all ${isSelected ? 'ring-4 ring-indigo-300' : ''}`} data-id="element-249">
              {/* Image Header */}
              <div className="relative h-32 overflow-hidden" data-id="element-250">
                <img src={ingredient.image} alt={ingredient.name} className="w-full h-full object-cover" data-id="element-251" />
                <div className={`absolute inset-0 bg-gradient-to-t ${ingredient.color} opacity-60`} data-id="element-252" />
                <div className="absolute top-3 right-3" data-id="element-253">
                  <div className={`w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg`} data-id="element-254">
                    <Icon className="w-5 h-5 text-gray-700" data-id="element-255" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4" data-id="element-256">
                <div className="flex items-start justify-between mb-2" data-id="element-257">
                  <div data-id="element-258">
                    <h4 className="font-bold text-gray-900 text-sm leading-tight" data-id="element-259">
                      {ingredient.name}
                    </h4>
                    <div className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r ${ingredient.color} text-white`} data-id="element-260">
                      {ingredient.dosage}
                    </div>
                  </div>
                </div>

                <AnimatePresence data-id="element-261">
                  {isSelected ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="mt-3 space-y-2" data-id="element-262">
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-263">
                        {ingredient.description}
                      </p>
                      <div className="space-y-1" data-id="element-264">
                        {ingredient.benefits.map((benefit, i) => <div key={i} className="flex items-center gap-2 text-xs text-gray-700" data-id="element-265">
                            <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${ingredient.color}`} data-id="element-266" />
                            {benefit}
                          </div>)}
                      </div>
                    </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="mt-2" data-id="element-267">
                      <div className="flex flex-wrap gap-1" data-id="element-268">
                        {ingredient.benefits.slice(0, 2).map((benefit, i) => <span key={i} className="text-xs text-gray-600 bg-white/50 px-2 py-0.5 rounded-full" data-id="element-269">
                            {benefit}
                          </span>)}
                      </div>
                      <div className="text-xs text-indigo-600 font-semibold mt-2" data-id="element-270">
                        Tap to learn more →
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Summary */}
      <div className="mt-8 bg-white rounded-xl p-6 shadow-lg" data-id="element-271">
        <div className="text-center" data-id="element-272">
          <p className="text-gray-700 mb-4" data-id="element-273">
            <strong data-id="element-274">Every ingredient is third-party tested</strong> and
            precisely balanced for maximum effectiveness
          </p>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-275">
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2" data-id="element-276">
              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20" data-id="element-277">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-278" />
              </svg>
              <span className="text-sm font-semibold text-green-700" data-id="element-279">
                Third-Party Tested
              </span>
            </div>
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-2" data-id="element-280">
              <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20" data-id="element-281">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-282" />
              </svg>
              <span className="text-sm font-semibold text-blue-700" data-id="element-283">
                Clinical Doses
              </span>
            </div>
            <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full px-4 py-2" data-id="element-284">
              <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20" data-id="element-285">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-286" />
              </svg>
              <span className="text-sm font-semibold text-purple-700" data-id="element-287">
                No Fillers
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}